import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, Phone, MapPin, ArrowUpRight, MessageCircle, Clock, Sparkles, Building2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { BRANCHES, UPCOMING_BRANCHES, buildWhatsAppLink } from "../lib/constants";
import { useTranslation } from "../lib/i18n";
import SectionHeading from "../components/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG =
  "https://images.pexels.com/photos/12820604/pexels-photo-12820604.jpeg";

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    branch: "auto",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const resolveBranch = () => {
    if (form.branch === "auto") return BRANCHES[0];
    return BRANCHES.find((b) => b.id === form.branch) || BRANCHES[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.form.errNameEmailMessage"));
      return;
    }
    setSubmitting(true);

    const branch = resolveBranch();
    const waMessage = `Hello Beyond Borders Travel (${branch.city}, ${branch.country}),

Name: ${form.name}
Email: ${form.email}
Phone: ${form.phone || "—"}

${form.message}`;

    try {
      await axios.post(
        `${API}/inquiry`,
        {
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          message: `[Branch: ${branch.city}, ${branch.country}]\n\n${form.message}`,
          branch: branch.id,
        },
        { timeout: 6000 }
      );
    } catch (err) {
      console.warn("Inquiry log failed (continuing):", err?.message);
    }

    const url = buildWhatsAppLink(waMessage, branch.whatsappNumber);
    window.open(url, "_blank", "noopener,noreferrer");

    toast.success(t("contact.form.successWA"), {
      description: t("contact.form.successDesc"),
    });
    setForm({ name: "", email: "", phone: "", message: "", branch: "auto" });
    setSubmitting(false);
  };

  return (
    <div data-testid="contact-page">
      <Toaster theme="dark" position="bottom-center" />

      <section className="relative h-[50vh] min-h-[380px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Tarmac at sunset" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#05070C] via-[#05070C]/80 to-[#05070C]/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05070C]/30 via-transparent to-[#05070C]" />
        </div>
        <div className="relative container-x pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="eyebrow mb-6">
              <span className="hairline" /> {t("contact.hero.eyebrow")}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05] max-w-3xl">
              {t("contact.hero.title")}{" "}
              <span className="gold-text italic">{t("contact.hero.titleAccent")}</span>
            </h1>
            <p className="mt-6 text-white/65 max-w-xl">
              {t("contact.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section">
        <div className="container-x">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <SectionHeading
              eyebrow={t("contact.branches.eyebrow")}
              title={t("contact.branches.title")}
              goldWord={t("contact.branches.title").split(" ").slice(-1)[0]}
            />
            <p className="text-white/55 text-sm lg:text-base max-w-md lg:text-right">
              {t("contact.branches.subtitle")}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
            {BRANCHES.map((b, i) => (
              <motion.article
                key={b.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`branch-card-${b.id}`}
                className="glass p-8 lg:p-10 relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 h-px w-24 bg-gold" />

                <div className="flex items-start justify-between gap-4 mb-8">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-3xl leading-none" aria-hidden="true">{b.flag}</span>
                      <span className="text-[11px] uppercase tracking-[0.3em] text-gold/85">
                        {b.country}
                      </span>
                    </div>
                    <h3 className="font-display text-3xl lg:text-4xl text-white leading-tight">
                      {b.city}
                    </h3>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-gold/80 border border-gold/40 px-3 py-1.5 rounded-full shrink-0">
                    <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
                    {t("contact.branch.openNow")}
                  </span>
                </div>

                <ul className="space-y-6">
                  <li className="flex gap-4">
                    <span className="grid place-items-center h-10 w-10 rounded-full border border-gold/30 text-gold shrink-0">
                      <MapPin className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 mb-1">
                        {t("contact.branch.office")}
                      </div>
                      <p className="text-white/85 leading-relaxed" data-testid={`branch-address-${b.id}`}>
                        {b.address}
                      </p>
                    </div>
                  </li>

                  <li className="flex gap-4">
                    <span className="grid place-items-center h-10 w-10 rounded-full border border-gold/30 text-gold shrink-0">
                      <Phone className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 mb-1">
                        {t("contact.branch.phone")}
                      </div>
                      {b.phones.map((p) => (
                        <a
                          key={p}
                          href={`tel:${p.replace(/\s+/g, "")}`}
                          data-testid={`branch-phone-${b.id}-${p.replace(/[^0-9]/g, "")}`}
                          className="block text-white/85 hover:text-gold transition-colors"
                        >
                          {p}
                        </a>
                      ))}
                    </div>
                  </li>

                  <li className="flex gap-4">
                    <span className="grid place-items-center h-10 w-10 rounded-full border border-gold/30 text-gold shrink-0">
                      <Mail className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 mb-1">
                        {t("contact.branch.email")}
                      </div>
                      {b.emails.map((e) => (
                        <a
                          key={e}
                          href={`mailto:${e}`}
                          data-testid={`branch-email-${b.id}-${e.split("@")[0]}`}
                          className="block text-white/85 hover:text-gold transition-colors break-all"
                        >
                          {e}
                        </a>
                      ))}
                    </div>
                  </li>

                  <li className="flex gap-4">
                    <span className="grid place-items-center h-10 w-10 rounded-full border border-gold/30 text-gold shrink-0">
                      <Clock className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 mb-1">
                        {t("contact.branch.hours")}
                      </div>
                      <p className="text-white/85">{b.hours}</p>
                      <p className="text-white/55 text-sm mt-1">{b.supportNote}</p>
                    </div>
                  </li>
                </ul>

                <a
                  href={buildWhatsAppLink(
                    `Hello Beyond Borders Travel (${b.city}, ${b.country}), I'd like to speak with a consultant.`,
                    b.whatsappNumber
                  )}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`branch-whatsapp-${b.id}`}
                  className="btn-ghost-gold w-full mt-10"
                >
                  <MessageCircle className="h-4 w-4" />
                  {t("contact.branch.chat", { city: b.city })}
                </a>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-[#0B1120]/40 border-y border-white/5">
        <div className="container-x">
          <div className="max-w-4xl mx-auto">
            <SectionHeading
              eyebrow={t("contact.branches.eyebrow")}
              title={t("contact.form.title")}
              goldWord={t("contact.form.title").split(" ").slice(-1)[0]}
              align="center"
            />
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="glass-strong p-8 sm:p-10 lg:p-12 relative mt-12"
            >
              <div className="absolute top-0 left-0 h-px w-24 bg-gold" />
              <h3 className="font-display text-3xl text-white mb-2">{t("contact.form.title")}</h3>
              <p className="text-white/55 text-sm mb-8">
                {t("contact.form.intro")}
              </p>

              {/* Branch router */}
              <div className="mb-8">
                <label className="text-[11px] uppercase tracking-[0.24em] text-white/55 mb-3 block">
                  {t("contact.form.branch")}
                </label>
                <div className="grid sm:grid-cols-3 gap-2" role="radiogroup" aria-label={t("contact.form.branch")}>
                  {[
                    { value: "auto", label: t("contact.form.branchAuto"), flag: null, subtitle: null },
                    ...BRANCHES.map((b) => ({
                      value: b.id,
                      label: b.city,
                      flag: b.flag,
                      subtitle: b.country,
                    })),
                  ].map((opt) => {
                    const active = form.branch === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setForm((f) => ({ ...f, branch: opt.value }))}
                        data-testid={`contact-branch-option-${opt.value}`}
                        className={`text-left p-4 border transition-all duration-300 ${
                          active
                            ? "border-gold bg-gold/10 text-white"
                            : "border-white/10 bg-transparent text-white/70 hover:border-gold/50 hover:text-white"
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          {opt.flag ? (
                            <span className="text-lg leading-none" aria-hidden="true">{opt.flag}</span>
                          ) : (
                            <Building2 className={`h-4 w-4 ${active ? "text-gold" : "text-white/45"}`} />
                          )}
                          <span className="text-sm font-medium truncate">{opt.label}</span>
                        </div>
                        {opt.subtitle && (
                          <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-white/45">
                            {opt.subtitle}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.24em] text-white/55 mb-2 block">{t("contact.form.fullName")}</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-input-name"
                    className="w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-white placeholder-white/30 transition-colors"
                    placeholder="Jane Banda"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.24em] text-white/55 mb-2 block">{t("contact.form.email")}</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-input-email"
                    className="w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-white placeholder-white/30 transition-colors"
                    placeholder="jane@company.com"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="text-[11px] uppercase tracking-[0.24em] text-white/55 mb-2 block">{t("contact.form.phone")}</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  data-testid="contact-input-phone"
                  className="w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-white placeholder-white/30 transition-colors"
                  placeholder="+265 ..."
                />
              </div>

              <div className="mt-6">
                <label className="text-[11px] uppercase tracking-[0.24em] text-white/55 mb-2 block">{t("contact.form.message")}</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  data-testid="contact-input-message"
                  className="w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-white placeholder-white/30 transition-colors resize-none"
                  placeholder={t("contact.form.messagePlaceholder")}
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="btn-gold mt-10 w-full sm:w-auto disabled:opacity-60"
              >
                {submitting ? t("contact.form.submitting") : t("contact.form.submit")}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="section" data-testid="upcoming-branches-section">
        <div className="container-x">
          <SectionHeading
            eyebrow={t("contact.upcoming.eyebrow")}
            title={t("contact.upcoming.title")}
            goldWord={t("contact.upcoming.title").split(" ").slice(-1)[0].replace(".", "")}
            subtitle={t("contact.upcoming.subtitle")}
            align="center"
          />

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mt-16">
            {UPCOMING_BRANCHES.map((u, i) => (
              <motion.div
                key={u.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                data-testid={`upcoming-branch-${u.id}`}
                className="group relative glass p-7 lg:p-9 text-center overflow-hidden"
              >
                <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em] text-gold/80 border border-gold/40 px-2.5 py-1 rounded-full">
                  <Sparkles className="h-2.5 w-2.5" />
                  {t("contact.upcoming.badge")}
                </span>
                <div className="text-5xl lg:text-6xl leading-none mb-6 mt-4" aria-hidden="true">
                  {u.flag}
                </div>
                <h4 className="font-display text-xl lg:text-2xl text-white">
                  {u.country}
                </h4>
                <p className="mt-3 text-[10px] uppercase tracking-[0.3em] text-white/45">
                  {t("contact.upcoming.card")}
                </p>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-white/45 text-sm mt-10 max-w-2xl mx-auto">
            {t("contact.upcoming.note")}
          </p>
        </div>
      </section>

      <section className="section bg-[#0B1120]/40 border-t border-white/5">
        <div className="container-x">
          <SectionHeading
            eyebrow={t("contact.maps.eyebrow")}
            title={t("contact.maps.title")}
            goldWord={t("contact.maps.titleAccent")}
            align="center"
          />
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 mt-12">
            {BRANCHES.map((b) => (
              <div key={b.id} data-testid={`branch-map-wrap-${b.id}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl leading-none" aria-hidden="true">{b.flag}</span>
                  <div>
                    <div className="font-display text-lg text-white">{b.city}</div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-white/45">{b.country}</div>
                  </div>
                </div>
                <div className="relative aspect-[16/10] overflow-hidden border border-white/10">
                  <iframe
                    title={b.mapTitle}
                    src={b.mapEmbedSrc}
                    className="absolute inset-0 w-full h-full grayscale-[40%] contrast-110 brightness-90"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid={`branch-map-${b.id}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
